/**
 * Column definitions for the Reports > Bounces blotter.
 *
 * Fixed arrangement: `COLUMN_ORDER` is the whole story — no ColumnConfigPopover, no column
 * preferences to apply, so `buildBounceColumns` takes only the hook-derived values the Action cell
 * closes over. A `render` is a plain renderer called per row by the Blotter and may not call hooks
 * itself, which is why the permission flag, the resent overrides and the Fix handler all arrive as
 * `deps` from the panel.
 *
 * `orDash` (EMPTY_CELL in the table) and `orBlank` ("" in the export) come from
 * `utils/reportsRedemptions-helper.ts` rather than being redeclared here.
 */
import BuildOutlined from "@mui/icons-material/BuildOutlined";
import type { CSSProperties } from "react";
import type { BlotterColumn } from "@/components/Blotter";
import CampaignStatusChip from "@/components/campaigns/CampaignStatusChip";
import { GfgButton } from "@/components/ui/GfgButton";
import {
  BOUNCES_TEXT,
  COLUMN_LABELS,
  COLUMN_ORDER,
  CSV_COLUMN_IDS,
} from "@/constants/reportsBounces";
import { EMPTY_CELL } from "@/constants/reportsRedemptions";
import type { BounceColumnId, BounceRow } from "@/interfaces/reportsBounces";
import type { CsvColumn } from "@/lib/csv";
import { bounceAction, bounceStatus } from "@/utils/reportsBounces-helper";
import { orBlank, orDash } from "@/utils/reportsRedemptions-helper";

/** Hook-derived values the Action cell closes over, resolved by the panel. */
export interface BounceColumnDeps {
  /** `can("send", "CAMPAIGN")` — without it the row offers no Fix at all. */
  canSend: boolean;
  /** Rows resent in this session, shown as Resent before the refetch brings their new status. */
  resentIds: ReadonlySet<number>;
  /** Asks the panel to open the Fix modal for this row; the panel owns the dialog state. */
  onFix: (row: BounceRow) => void;
}

/** Every column except the interactive `action` cell — the ones with a plain-text value. */
type BounceDataColumnId = Exclude<BounceColumnId, "action">;

const MUTED: CSSProperties = { color: "var(--color-graphite)" };
const PRIMARY: CSSProperties = { fontWeight: 600 };

/** One data column's plain text: the CSV's value and the table's sort payload, from one source. */
const DATA_TEXT: Record<BounceDataColumnId, (row: BounceRow) => string> = {
  campaign: (r) => r.campaignName,
  recipientName: (r) => r.recipientName,
  recipientEmail: (r) => orBlank(r.recipientEmail),
  recipientCompany: (r) => orBlank(r.recipientCompany),
  bounceType: (r) => (r.bounceType ? BOUNCES_TEXT.bounceType[r.bounceType] : ""),
  status: (r) => r.status,
};

function isDataColumn(id: BounceColumnId): id is BounceDataColumnId {
  return id !== "action";
}

/** The Action cell for one row — the decision itself lives in `bounceAction`, not here. */
function renderAction(row: BounceRow, deps: BounceColumnDeps) {
  const action = bounceAction(row, {
    canSend: deps.canSend,
    resentLocally: deps.resentIds.has(row.id),
  });

  switch (action) {
    case "ended":
      return <span style={MUTED}>{BOUNCES_TEXT.action.ended}</span>;
    case "fix":
      return (
        <GfgButton
          type="button"
          variant="text"
          startIcon={<BuildOutlined sx={{ fontSize: 16 }} />}
          onClick={() => deps.onFix(row)}
        >
          {BOUNCES_TEXT.action.fix}
        </GfgButton>
      );
    case "none":
      return EMPTY_CELL;
  }
}

/** Every column keyed by id, so `COLUMN_ORDER` alone decides what the table shows and in which order. */
function buildColumnMap(deps: BounceColumnDeps): Record<BounceColumnId, BlotterColumn<BounceRow>> {
  return {
    campaign: {
      key: "campaign",
      label: COLUMN_LABELS.campaign,
      sortable: true,
      sortValue: DATA_TEXT.campaign,
      exportValue: DATA_TEXT.campaign,
      render: (r) => r.campaignName,
    },
    recipientName: {
      key: "recipientName",
      label: COLUMN_LABELS.recipientName,
      sortable: true,
      sortValue: DATA_TEXT.recipientName,
      exportValue: DATA_TEXT.recipientName,
      render: (r) => <span style={PRIMARY}>{r.recipientName}</span>,
    },
    recipientEmail: {
      key: "recipientEmail",
      label: COLUMN_LABELS.recipientEmail,
      sortable: true,
      sortValue: DATA_TEXT.recipientEmail,
      exportValue: DATA_TEXT.recipientEmail,
      render: (r) => <span style={MUTED}>{orDash(r.recipientEmail)}</span>,
    },
    recipientCompany: {
      key: "recipientCompany",
      label: COLUMN_LABELS.recipientCompany,
      sortable: true,
      sortValue: DATA_TEXT.recipientCompany,
      exportValue: DATA_TEXT.recipientCompany,
      render: (r) => orDash(r.recipientCompany),
    },
    bounceType: {
      // Not sortable: the API's whitelist has no expression for it.
      key: "bounceType",
      label: COLUMN_LABELS.bounceType,
      exportValue: DATA_TEXT.bounceType,
      render: (r) => (r.bounceType ? BOUNCES_TEXT.bounceType[r.bounceType] : EMPTY_CELL),
    },
    status: {
      // Not sortable: derived per row from resentAt and the redemption, with no sort expression.
      key: "status",
      label: COLUMN_LABELS.status,
      exportValue: DATA_TEXT.status,
      render: (r) => (
        <CampaignStatusChip status={bounceStatus(r, deps.resentIds.has(r.id))} size="sm" />
      ),
    },
    action: {
      key: "action",
      label: COLUMN_LABELS.action,
      render: (r) => renderAction(r, deps),
    },
  };
}

/** The fixed columns, in `COLUMN_ORDER`. */
export function buildBounceColumns(deps: BounceColumnDeps): BlotterColumn<BounceRow>[] {
  const columns = buildColumnMap(deps);
  return COLUMN_ORDER.map((id) => columns[id]);
}

/**
 * The CSV's columns, passed explicitly to the Blotter rather than derived from the visible ones:
 * the export carries the data columns and never the Action cell, which has no text at all.
 */
export function buildBounceCsvColumns(): CsvColumn<BounceRow>[] {
  return CSV_COLUMN_IDS.filter(isDataColumn).map((id) => ({
    label: COLUMN_LABELS[id],
    value: DATA_TEXT[id],
  }));
}
